import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ApiService } from "../services/api";
import AuthModal from "../auth/AuthModal";
import "./dashboard.scss";

// View Imports
import Home from "../home/home";
import CreateVideo from "../create_video/create_video";
import Pricing from "../pricing/pricing";
import DownloadApp from "../download_app/download_app";
import VideoResult from "../create_video/video_result";
import MyCreations from "../my_creations/my_creations";
import CreateStory from "../create_story/create_story";
import PrivacyPolicy from "../privacy_policy/privacy-policy";
import CreateImage from "../generate_image/create_image";
import Disclaimer from "../disclaimer/disclaimer";
import UserResponsibility from "../user_responsibility/user_responsibility";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [credits, setCredits] = useState(0);
  const [user, setUser] = useState(null);
  const logoSrc = `${process.env.PUBLIC_URL || ""}/zullu_logo.png`;

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // UPDATED: Logic to handle both Google (Email) and OTP (Phone) users
  const fetchUserData = async () => {
    const phone = localStorage.getItem("phoneNumber");
    const email = localStorage.getItem("userEmail");

    // Identifier is either email or phone (Option A)
    const identifier = email || phone;

    if (!identifier) {
      setIsLoggedIn(false);
      setUser(null);
      return;
    }

    try {
      const response = await ApiService.getUserInfo(identifier);
      if (response && response.success) {
        setIsLoggedIn(true);
        setUser(response.data);
        setCredits(response.data.availableCredits || 0);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [location.pathname]);

  useEffect(() => {
    const refreshUserCredits = () => fetchUserData();
    window.addEventListener("zulluCreditsUpdated", refreshUserCredits);
    return () =>
      window.removeEventListener("zulluCreditsUpdated", refreshUserCredits);
  });

  // UPDATED: Clear all keys related to both login methods
  const handleLogout = () => {
    localStorage.removeItem("phoneNumber");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("token");
    localStorage.removeItem("userName");

    setIsLoggedIn(false);
    setUser(null);
    setCredits(0);
    setShowProfileModal(false);
    navigate("/");
  };

  const menuItems = [
    { id: "home", label: "Home", icon: "fi-rr-home", path: "/" },
    {
      id: "generate",
      label: "Create Video",
      icon: "fi-rr-magic-wand",
      path: "/create-video",
    },
    {
      id: "image",
      label: "Create Image",
      icon: "fi-rr-picture",
      path: "/create-image",
    },
    {
      id: "story",
      label: "Create Story (Free)",
      icon: "fi-rr-document",
      path: "/create-story",
    },
    {
      id: "my-creations",
      label: "My Creations",
      icon: "fi-rr-time-past",
      path: "/my-creations",
    },
    { id: "pricing", label: "Pricing", icon: "fi-rr-box", path: "/pricing" },
    { id: "disclaimer", label: "Disclaimer", icon: "fi-rr-info", path: "/disclaimer" },
    { id: "app", label: "App", icon: "fi-rr-smartphone", path: "/download" },
    
  ];

  return (
    <div className="zullu-layout">
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={() => {
          setShowAuthModal(false);
          fetchUserData();
        }}
      />

      <AnimatePresence>
        {showProfileModal && isLoggedIn && (
          <div className="pm-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="pm-card"
            >
              <button
                className="pm-close"
                onClick={() => setShowProfileModal(false)}
              >
                &times;
              </button>
              <div className="pm-user-info">
                <div className="pm-avatar-large">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="Profile" />
                  ) : (
                    user?.name?.charAt(0) || "U"
                  )}
                </div>
                <h2>{user?.name}</h2>
                <p>
                  {localStorage.getItem("userEmail") ||
                    localStorage.getItem("phoneNumber")}
                </p>
              </div>
              <div className="pm-stats-row">
                <div className="pm-stat">
                  <span>Credits</span>
                  <b>⚡ {credits}</b>
                </div>
                <div className="pm-stat">
                  <span>Status</span>
                  <b className="active-tag">Pro Member</b>
                </div>
              </div>
              <div className="pm-actions">
                <button className="pm-btn-logout" onClick={handleLogout}>
                  <i className="fi fi-rr-exit"></i> Logout Account
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMobile && isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      <aside
        className={`z-sidebar ${isSidebarOpen ? "expanded" : "collapsed"} ${
          isMobile && isSidebarOpen ? "mobile-visible" : ""
        }`}
      >
        <div className="sidebar-brand">
          <img src={logoSrc} alt="Zullu Logo" />
          {(isSidebarOpen || isMobile) && (
            <span>
              ZULLU<span>AI</span>
            </span>
          )}
        </div>

        <nav className="sidebar-menu">
          {menuItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `menu-link ${isActive ? "active" : ""}`
              }
            >
              <i className={`fi ${item.icon}`}></i>
              {(isSidebarOpen || isMobile) && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div
          className="sidebar-user"
          onClick={() =>
            isLoggedIn ? setShowProfileModal(true) : setShowAuthModal(true)
          }
        >
          <div className="user-avatar">
            {isLoggedIn ? (
              user?.avatar ? (
                <img
                  src={user.avatar}
                  alt="User"
                  style={{
                    borderRadius: "50%",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                user?.name?.charAt(0) || "U"
              )
            ) : (
              <i className="fi fi-rr-user"></i>
            )}
          </div>
          {(isSidebarOpen || isMobile) && (
            <div className="user-meta">
              <b>{isLoggedIn ? user?.name : "Login / Signup"}</b>
              <p>{isLoggedIn ? "Manage Account" : "Access AI"}</p>
            </div>
          )}
        </div>
      </aside>

      <div
        className={`z-main ${
          isSidebarOpen && !isMobile ? "shifted" : "full-width"
        }`}
      >
        <header className="z-header">
          <div className="header-left">
            <button
              className="menu-trigger"
              onClick={() => setSidebarOpen(!isSidebarOpen)}
            >
              <i className="fi fi-rr-menu-burger"></i>
            </button>
          </div>
          <div className="header-right">
            {isLoggedIn && <div className="credits-badge">⚡ {credits}</div>}
            <div
              className="profile-trigger"
              onClick={() =>
                isLoggedIn ? setShowProfileModal(true) : setShowAuthModal(true)
              }
            >
              <img
                className="header-avatar"
                src={
                  isLoggedIn
                    ? user?.avatar ||
                      `https://ui-avatars.com/api/?name=${user?.name}&background=a349ff&color=fff`
                    : `https://cdn-icons-png.flaticon.com/512/149/149071.png`
                }
                style={{ borderRadius: "50%", objectFit: "cover" }}
                alt="Profile"
              />
            </div>
          </div>
        </header>

        <div className="z-content-canvas">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/create-video" element={<CreateVideo />} />
            <Route path="/create-video/:mode" element={<CreateVideo />} />
            <Route path="/my-creations" element={<MyCreations />} />
            <Route path="/my-creations/:type" element={<MyCreations />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/download" element={<DownloadApp />} />
            <Route path="/create-image" element={<CreateImage />} />
            <Route path="/create-story" element={<CreateStory />} />
            <Route path="/video-result/:jobId" element={<VideoResult />} />
            <Route path="/video-result/:provider/:jobId" element={<VideoResult />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/user-responsibility" element={<UserResponsibility />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
